import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { BrandModel, VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

type DataLayerItem = {
    item_id: string;
    item_name: string;
    index: number;
    item_brand: string | BrandModel;
    item_category: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    price: number | undefined;
    quantity: number | undefined;
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        settingsCollection: getSettingsCollection(state),
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
            ${GridOffset} {
                max-width: 100%;
                display: flex;
                flex-direction: column;
                ${GridItemStyle} {
                    padding: 0px;
                }
            }
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                ${TypographyStyle} {
                    font-size: 16px;
                    color: ${getColor("primary")};
                    height: auto;
                    margin-bottom: 5px;
                    line-height: 1.125rem;
                }
            `,
            typographyProps: {
                css: css`
                    width: 100%;
                    ${wrapInContainerStyles}
                `,
            },
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            padding: 5px 0px;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 5px 0px;
        `,
    },
    partNumbersStyles: {
        manufacturerItemLabelText: {
            css: css`
                white-space: nowrap;
                font-size: 14px;
                color: ${getColor("text.accent")};
                margin-right: 2px;
            `,
        },
        manufacturerItemValueText: {
            css: css`
                white-space: normal;
                font-size: 14px;
                /* margin-left: 2px !important; */
                color: ${getColor("text.accent")};
            `,
        },
        customerNameValueText: {
            css: css`
                white-space: normal;
                font-size: 14px;
                color: ${getColor("text.accent")};
                /* margin-left: 2px !important; */
            `,
        },
        customerNameLabelText: {
            css: css`
                white-space: nowrap;
                font-size: 14px;
                color: ${getColor("text.accent")};
                /* margin-left: 2px !important; */
            `,
        },
        erpNumberLabelText: {
            css: css`
                white-space: nowrap;
                font-size: 14px;
                color: ${getColor("text.accent")};
                margin-right: 2px;
            `,
        },
        erpNumberValueText: {
            css: css`
                white-space: normal;
                font-size: 14px;
                /* margin-left: 2px !important; */
                color: ${getColor("text.accent")};
            `,
        },
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding: 5px 0px;
            /* margin-bottom: 5px !important; */
        `,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
        css: css`
            max-height: 150px;
            overflow-y: auto;
            ${GridWrapper} {
                ${GridOffset} {
                    display: flex;
                    flex-direction: row;
                }
            }
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    availabilityStyles: {
        container: {
            gap: 0,
            css: css`
                flex-grow: 0;
                > ${GridOffset} {
                    display: flex;
                    flex-direction: column;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                flex-direction: column;
                            `,
                            css`
                                flex-direction: column;
                            `,
                            css`
                                flex-direction: column;
                            `,
                            null,
                            null,
                        ])}
                    > ${GridItemStyle} {
                        flex-basis: max-content;
                        max-width: max-content;
                        align-items: center;
                        padding: 0px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                null,
                            ])}
                    }
                }
            `,
        },
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    category,
    productListCatalogPage,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    const verifyEndpoint = () => {
        const urlArray = window.location.href.split("/");
        const endpoint = urlArray[urlArray.length - 1];
        return endpoint.toLowerCase().includes("search?");
    };

    const addItemListId = () => {
        if (verifyEndpoint()) {
            return "Search";
        }
        return category?.id;
    };

    const addItemListName = () => {
        if (verifyEndpoint()) {
            return "Search";
        }
        return category?.name;
    };

    const generateItemsList = () => {
        const products = productContext.product;
        const itemsArr: DataLayerItem[] = [];

        if (products) {
            const categoryArr = productListCatalogPage?.uri?.split("?")[1]?.split("/") || [];
            const categories = categoryArr?.slice(2, categoryArr.length);
            const categorys = categories.length === 0 ? "Search" : category?.name;
            const categoryId = addItemListId();
            const categoryName = addItemListName();
            // eslint-disable-next-line array-callback-return
            itemsArr.push({
                item_id: product.productNumber || "",
                item_name: product.productTitle || "",
                index: 0,
                item_brand: product.brand?.name || "",
                item_category: categorys || "",
                item_list_id: categoryId,
                item_list_name: categoryName,
                price: productContext.productInfo.pricing?.unitNetPrice,
                quantity: productContext.productInfo.inventory?.qtyOnHand,
            });
        }
        return itemsArr;
    };

    const addToDataLayer = () => {
        const selected_items = generateItemsList();
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "select_item",
                ecommerce: {
                    selected_item_list_id: addItemListId(),
                    selected_item_list_name: addItemListName(),
                    selected_items,
                },
            });
        }
    };
    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription
                        onClick={() => addToDataLayer()}
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                    />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
